/**
 * The shared react-i18next runtime factory. Each app bundle calls
 * `createArchI18n` once at boot with its own bundled catalogs; that initialises
 * the *default* i18next instance, so `useTranslation()` works everywhere with
 * no `<I18nextProvider>` wrapper. Resources are bundled (not fetched) and there
 * is no async backend, so init resolves synchronously — the first render
 * already has copy.
 */

import i18n from "i18next";
import type { Resource } from "i18next";
import ICU from "i18next-icu";
import { initReactI18next, useTranslation } from "react-i18next";
import {
  DEFAULT_LOCALE,
  isLocale,
  type LocaleId,
  persistLocale,
  resolveInitialLocale,
} from "./locale.js";

export interface CreateArchI18nOptions {
  /** `{ en: { ns: {...} }, ru: { ns: {...} } }` — the app's bundled catalogs. */
  readonly resources: Resource;
  readonly ns: readonly string[];
  readonly defaultNS: string;
  /** Explicit startup locale (plugin webview `?locale=`); overrides detection. */
  readonly localeOverride?: string | null;
}

/** Initialise the default i18next instance for this app bundle. Idempotent. */
export function createArchI18n(opts: CreateArchI18nOptions): typeof i18n {
  const lng = resolveInitialLocale(opts.localeOverride);
  persistLocale(lng);
  if (!i18n.isInitialized) {
    void i18n
      .use(ICU)
      .use(initReactI18next)
      .init({
        resources: opts.resources,
        lng,
        fallbackLng: DEFAULT_LOCALE,
        ns: opts.ns as string[],
        defaultNS: opts.defaultNS,
        // React already escapes; i18next must not double-escape.
        interpolation: { escapeValue: false },
        // No Suspense boundary in these trees — render synchronously.
        react: { useSuspense: false },
        returnNull: false,
      });
  }
  return i18n;
}

/** The live locale, or the default before init. */
export function getLocale(): LocaleId {
  return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}

/** Switch locale everywhere: persist, reflect `<html lang>`, re-render. */
export function setLocale(id: LocaleId): void {
  persistLocale(id);
  void i18n.changeLanguage(id);
}

/**
 * Read the active locale reactively and switch it. Re-renders on change
 * (react-i18next subscribes to `languageChanged` under the hood).
 */
export function useLocale(): { locale: LocaleId; setLocale: (id: LocaleId) => void } {
  const { i18n: inst } = useTranslation();
  const locale = isLocale(inst.language) ? inst.language : DEFAULT_LOCALE;
  return { locale, setLocale };
}
